import React, { FC, useState } from "react";
import { Tabs } from "antd";
import { useSelector } from "react-redux";
import TaskInformationById from "./TaskInformationById";
import TaskQuickViewCard from "./TaskQuickViewCard";
import SubTaskList from "./SubTaskList";
import TaskStatusTimeLine from "./TaskStatusTimeLine";
import TaskActivityStreamLine from "./TaskActivityStreamLine";
import TaskCheckListView from "./TaskCheckListView";
import { CommentsTab } from "../../../components/comments/Comments";
import { TimeLogTable } from "../timeSheets/TimeLogTable";
import { IssuesTable } from "../issues/IssueTableView";
import TaskDependencies from "../tasks/taskDependenciesAnt";
import DocumentsTabWrapper from "../projectDocument/DocumentsTabWrapper";
import { RootState } from "../../../store/store";

interface TabItem {
  label: string;
  tabKey: string;
  content: React.ReactNode;
}

const TaskInDetailView: FC<{
  id: number;
  activeTab: string;
  refreshData?: () => void;
  handleClose?: () => void;
}> = ({ id, activeTab, refreshData = () => {}, handleClose = () => {} }) => {
  const [tab, setTab] = useState(activeTab);
  const [viewMode, setViewMode] = useState<"summary" | "edit">("summary");

  const { data: TaskListInDetailModal } = useSelector(
    (state: RootState) => state.TaskListInDetailModal
  );

  const { selectedTaskDetails } = TaskListInDetailModal || {};

  const tabs: TabItem[] = [
    {
      label: "View",
      tabKey: "view",
      content: (
        <div>
          {viewMode === "summary" ? (
            <TaskQuickViewCard
              onEdit={() => setViewMode("edit")}
              onRefresh={refreshData}
            />
          ) : (
            <TaskInformationById
              refreshData={refreshData}
              handleClose={handleClose}
              isFromWorkUI={false}
              hideInternalTabs={true}
              initialEditMode={true}
              onBackToSummary={() => setViewMode("summary")}
            />
          )}
        </div>
      ),
    },
    {
      label: "Comments",
      tabKey: "comments",
      content: (
        <div>
          <CommentsTab
            module="Task"
            moduleId={selectedTaskDetails?.taskId || id}
            projectId={selectedTaskDetails?.projectId}
            projectName={selectedTaskDetails?.projectName}
            name={selectedTaskDetails?.taskName}
            showTaskModal={false}
            isFromWorkUI={false}
          />
        </div>
      ),
    },
    {
      label: "Sub Tasks",
      tabKey: "subtasks",
      content: (
        <div>
          <SubTaskList isActive={tab === "subtasks"} isFromWorkUI={false} />
        </div>
      ),
    },
    {
      label: "Log Hours",
      tabKey: "loghours",
      content: (
        <div>
          <TimeLogTable
            projectId={selectedTaskDetails?.projectId}
            taskId={selectedTaskDetails?.taskId || id}
            activeTab={tab}
            isFromWorkUI={false}
            isFromSpecificLevel={true}
            taskOwnerId={selectedTaskDetails?.taskOwnerId}
            allowTaskUpdates={selectedTaskDetails?.allowTaskUpdates !== false}
          />
        </div>
      ),
    },
    {
      label: "Issues",
      tabKey: "issues",
      content: (
        <div>
          <IssuesTable
            projectId={selectedTaskDetails?.projectId}
            taskId={selectedTaskDetails?.taskId || id}
            activeTab={tab}
            isFromWorkUI={false}
            isFromSpecificLevel={true}
            isFromTaskDetailPage={true}
          />
        </div>
      ),
    },
    {
      label: "Dependencies",
      tabKey: "dependencies",
      content: (
        <div>
          <TaskDependencies
            loadOnRefresh={tab}
            taskId={selectedTaskDetails?.taskId || id}
            isFromWorkUI={false}
            projectId={selectedTaskDetails?.projectId}
          />
        </div>
      ),
    },
    {
      label: "Checklist",
      tabKey: "checklist",
      content: (
        <div>
          <TaskCheckListView
            checklistResponses={selectedTaskDetails?.checklistLinks || []}
            task={selectedTaskDetails}
            refreshData={refreshData}
            isFromWorkUI={false}
          />
        </div>
      ),
    },
    {
      label: "Documents",
      tabKey: "documents",
      content: (
        <div>
          <DocumentsTabWrapper
            linkType="task"
            shouldLoadData={tab === "documents"}
            isFromWorkUI={false}
          />
        </div>
      ),
    },
    {
      label: "Timeline",
      tabKey: "timeline",
      content: (
        <div>
          <TaskStatusTimeLine isActive={tab === "timeline"} />
        </div>
      ),
    },
    {
      label: "Activity",
      tabKey: "activitystream",
      content: (
        <div>
          <TaskActivityStreamLine activeTab={tab} />
        </div>
      ),
    },
  ];

  return (
    <Tabs
      className="pms-tabs"
      activeKey={tab}
      onChange={(key) => {
        // When returning to View from another tab, refetch the task so
        // cross-tab edits (subtasks, log hours, checklist, etc.) are
        // reflected without a manual page refresh.
        if (key === "view" && tab !== "view" && typeof refreshData === "function") {
          refreshData();
        }
        setTab(key);
      }}
      // The legacy nav rendered only the active tab's content — inactive
      // tabs were unmounted on switch. destroyInactiveTabPane keeps those
      // mount/unmount semantics (mount on activate, unmount on leave).
      destroyInactiveTabPane
      items={tabs.map((tabItem) => ({
        key: tabItem.tabKey,
        label: tabItem.label,
        children: tabItem.content,
      }))}
    />
  );
};

export default TaskInDetailView;
